import React, { useCallback, useEffect, useState } from 'react';
import CardCommon from '@op/components/CardCommon';
import {
  GptRequestType,
  PromptConfirmItemType,
} from '@op/components/GPT/types';
import SvgIcon from '@home/components/SvgIcon';
import { downloadAction } from '@op/pages/download/utils';
import GPT from '@op/components/GPT';
import useGeySummary from '@op/components/GPT/hooks/useGetSummary';
import { DBO_T } from '@op/utils/format';
import {
  ReportHeader,
  ReportHeaderTitle,
} from '../CustomAISummary/component/GPTSummaryPreview';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';
import useLanguage from '@op/hooks/useLanguage';
import './index.scss';
import { getBasePath } from '@/utils/utils';

export type ReportGptProps = {
  wholeGameInfo: any;
  contentRef: any;
  scrolling: boolean;
  gameInfo: any;
  task: any;
  pageId?: string;
  requestData?: any;
  pageName?: string;
};

export function GPTBody({
  filter,
  contentRef,
  commentList,
  socialList,
  promptInfo,
  gameInfo,
  loadingElement,
  saveClick,
  cardSaveIcon,
  hasStopBtn = true,
  stopClick,
  saveTipsIcon,
  ...props
}: {
  filter: any;
  contentRef: any;
  commentList: any;
  socialList: any;
  promptInfo: PromptConfirmItemType;
  gameInfo: any;
  loadingElement?: any;
  saveClick?: any;
  cardSaveIcon?: any;
  hasStopBtn?: boolean;
  stopClick?: any;
  saveTipsIcon?: any;
  extraFloatIcon?: React.ReactNode;
  [key: string]: any;
}) {
  const { formatMessage } = useLanguage();
  const { getSummary, summaryRef, htmlSummaryRef } = useGeySummary({});
  const [hasResult, setHasResult] = useState(false);
  const [downloadFiles, setDownloadFiles] = useState<any>({});

  useEffect(() => {
    if (promptInfo.taskId) {
      setTimeout(() => {
        getSummary(promptInfo);
      }, 500);
    }
  }, [promptInfo]);

  const downloadAdvanceComments = useCallback(() => {
    if (downloadFiles && Object.keys(downloadFiles).length > 0) {
      downloadAction({
        key: 'GptFileZipDownload',
        fileName: 'Advanced AI Report Reviews',
        filter,
        auxiliaryParams: { task_id: promptInfo.taskId },
      });
    } else {
      window.open(
        `${getBasePath()}/opinion/NewDownload?gameid=${gameInfo?.edition_unified_id
        }&task_id=${promptInfo.taskId}&type=advancedGptData`,
        '_blank',
      );
    }
  }, [filter, promptInfo, downloadFiles, gameInfo]);

  const [show, setShow] = useState(true);

  if (!promptInfo.taskId) return null;

  return (
    <div className={`dbo-gpt-summary-preview-content`}>
      <GPT.PageCard
        gameInfo={gameInfo}
        filter={filter}
        ref={summaryRef}
        loadingHeight={118}
        className="markdown-body"
        showModelType={false}
        hideIcons={['refresh', 'fullscreen']}
        loadingElement={loadingElement}
        saveClick={saveClick}
        summaryHtmlRef={htmlSummaryRef}
        hasStopBtn={hasStopBtn}
        saveIcon={cardSaveIcon}
        leftElement={{
          gap: 0,
          span: 24,
          element: (
            <ReportHeader
              formatMessage={formatMessage}
              filter={filter}
              commentList={commentList}
              socialList={socialList}
            />
          ),
          textSpan: 24,
        }}
        stopAction={() => stopClick?.()}
        title={
          <ReportHeaderTitle
            promptInfo={promptInfo}
            downloadAdvanceComments={downloadAdvanceComments}
            formatMessage={formatMessage}
            setShow={setShow}
            hasResult={hasResult}
          />
        }
        alwaysShowOperations
        backGPTText={(value: string | GptRequestType) => {
          if (typeof value === 'string') {
            setHasResult(value !== '');
          } else {
            setDownloadFiles(value?.files || {});
            setHasResult(value.task_status === 'success');
          }
        }}
        hasDownload={promptInfo?.gptOptions === 'advanced' && hasResult && downloadAdvanceComments}
        handleDownloadOnClick={() => downloadAdvanceComments?.()}
        {...props}
      >
        {() => { }}
      </GPT.PageCard>

      <div className={`dbo-button-float ${!show ? '' : '--hide'}`}>
        {props?.extraFloatIcon}
        {promptInfo.gptOptions === 'advanced' && (
          <div className="dbo-button-float-download">
            <SvgIcon
              type="download-icon"
              onClick={() => {
                downloadAdvanceComments();
                buriedBtnPoint('DownloadComments');
              }}
            />
          </div>
        )}
        <div className="dbo-button-float-copy">
          <SvgIcon
            onClick={() =>
              void DBO_T.copy(htmlSummaryRef.current, formatMessage)
            }
            type="datalab-copy"
          />
        </div>
        {saveTipsIcon}
      </div>
    </div>
  );
}

export function CustomGptBody({
  filter,
  contentRef,
  gameInfo,
  commentList,
  socialList,
  promptInfo,
  loading,
  ...props
}: ReportGptProps & {
  filter: any;
  commentList: any;
  socialList: any;
  promptInfo?: PromptConfirmItemType;
  loading: boolean;
  [key: string]: any;
}) {
  if (!promptInfo?.taskId) {
    return null;
  }

  return (
    <CardCommon
      contentCard
      ref={contentRef}
      loading={loading || !filter}
      loadingHeight={500}
    >
      <GPTBody
        gameInfo={gameInfo}
        contentRef={contentRef}
        filter={filter}
        promptInfo={promptInfo as PromptConfirmItemType}
        commentList={commentList}
        socialList={socialList}
        needRequestPrompt={false}
        {...props}
      />
    </CardCommon>
  );
}
